import { router } from '@inertiajs/react';
import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { Button } from '@/Components/Button';
import { ExclamationSvg } from '@/Components/Icons/ExclamationSvg';
import { LogoSvg } from '@/Components/Icons/LogoSvg';
import { InputLabel } from '@/Components/InputLabel';
import { ModalView } from '@/Components/ModalView';
import TextInput, { TextInputHandle } from '@/Components/TextInput';
import { useToast } from '@/hook/useToast';
import { useSetReelName } from '@/store/reels/reels.hook';
import { AppModalProps } from '@/types/modal.type';

type ReelRenameModalProps = AppModalProps<{
    reelId: number;
    reelCode: string;
    initialValue: string;
}>;

export const ReelRenameModal: FC<ReelRenameModalProps> = ({
    payload,
    onClose,
}) => {
    const reelId = payload?.reelId;
    const reelCode = payload?.reelCode;
    const initialValue = payload?.initialValue;

    const renameReel = useSetReelName();
    const { showToast } = useToast();

    const [value, setValue] = useState<string>(initialValue || '');
    const inputRef = useRef<TextInputHandle>(null);

    const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    }, []);

    useEffect(() => {
        if (initialValue && typeof initialValue === 'string') {
            setValue(initialValue);
        }
    }, [initialValue]);

    const focusInput = () => {
        inputRef.current?.focus();
        inputRef.current?.select();
    };

    const onSave = useCallback(() => {
        onClose();

        renameReel(reelCode, value);

        router.patch(
            route('reels.update', reelId),
            { name: value },
            {
                async: true,
                showProgress: false,
                onSuccess: () => {
                    showToast({
                        variant: 'success',
                        label: 'Changes saved successfully',
                    });
                },
                onError: () => {
                    renameReel(reelCode, initialValue);

                    showToast({
                        variant: 'danger',
                        label: 'Name could not be updated',
                        autoClose: false,
                    });
                },
            },
        );
    }, [reelId, reelCode, value, initialValue, onClose]);

    return (
        <ModalView
            onAnimationStart={focusInput}
            maxWidth="sm"
            onSubmit={onSave}
            onClose={onClose}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="flex flex-row items-center justify-center">
                <LogoSvg className="mb-8 -mr-1.5" />
                <ExclamationSvg className="[&_path]:fill-brand-teal" />
            </div>
            <InputLabel
                htmlFor="reel-name"
                value="Enter name of your video reel"
                className="w-full text-left mb-2 mt-2"
            />
            <TextInput
                id="reel-name"
                ref={inputRef}
                placeholder="Video Reel Name"
                className="w-full mb-4"
                value={value}
                onChange={onChange}
                onKeyDown={(e) => e.key === 'Enter' && onSave()}
            />
            <div className="mt-2 w-full flex flex-row items-center justify-center space-x-3">
                <Button
                    className="flex-1 h-9"
                    variant="default"
                    onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    className="flex-1 h-9"
                    variant="primary"
                    onClick={onSave}>
                    Continue
                </Button>
            </div>
        </ModalView>
    );
};
